export default function interactiveSitePlan(config = {}) {
    return {
        buildings: config.buildings || [],
        activeIndex: null,
        playing: false,
        reversing: false,
        ready: false,
        loading: false,
        videoVisible: false,
        failedIndex: null,
        playbackRunId: 0,
        loadTimer: null,
        frameCallbackId: null,
        frameVideo: null,
        warmTimer: null,
        retainedVideos: [],
        visibilityHandler: null,
        pageHideHandler: null,
        reverseSpeed: config.reverseSpeed || 2,
        reverseRunId: 0,
        rafId: null,
        reverseSeekVideo: null,
        reverseSeekHandler: null,
        reverseSeekTimer: null,
        contentIndex: null,
        contentVisible: false,
        contentDelay: config.contentDelay ?? 3000,
        contentFade: config.contentFade ?? 600,
        contentShowTimer: null,
        contentHideTimer: null,
        openIndex: null,
        mobileDetailsOpen: false,
        modalOpen: false,
        modalClosing: false,
        modalCloseTimer: null,
        modalFadeDuration: config.modalFadeDuration ?? 250,
        posterAspect:
            config.posterWidth && config.posterHeight
                ? config.posterWidth / config.posterHeight
                : 16 / 9,
        stageWidth: 0,
        stageHeight: 0,
        stageResizeObserver: null,

        init() {
            this.$nextTick(() => {
                this.initStageSizing();

                this.ready = true;
            });
            this.visibilityHandler = () => {
                if (document.hidden) this.resetExperience();
            };
            this.pageHideHandler = () => this.resetExperience();
            document.addEventListener('visibilitychange', this.visibilityHandler);
            window.addEventListener('pagehide', this.pageHideHandler);
        },

        destroy() {
            this.stopReverse();
            this.clearContentTimers();
            if (this.modalCloseTimer) clearTimeout(this.modalCloseTimer);
            this.stageResizeObserver?.disconnect();
            this.resetExperience();
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            window.removeEventListener('pagehide', this.pageHideHandler);
            this.retainedVideos.forEach(index => this.releaseVideo(index));
        },

        openExperience() {
            const dialog = this.$refs.dialog;
            if (!dialog || dialog.open) return;

            if (this.modalCloseTimer) clearTimeout(this.modalCloseTimer);
            this.modalCloseTimer = null;
            this.modalClosing = false;
            dialog.classList.remove('is-closing');
            dialog.showModal();
            this.modalOpen = true;
            this.$nextTick(() => this.updateStageSize());
        },

        closeExperience() {
            const dialog = this.$refs.dialog;
            if (!dialog?.open || this.modalClosing) return;

            this.modalOpen = false;
            this.resetExperience();
            this.modalClosing = true;
            dialog.classList.add('is-closing');

            const reduceMotion = window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            ).matches;
            if (reduceMotion) {
                dialog.close();
                return;
            }

            this.modalCloseTimer = setTimeout(() => {
                this.modalCloseTimer = null;
                if (dialog.open) dialog.close();
            }, this.modalFadeDuration);
        },

        handleModalClose() {
            if (this.modalCloseTimer) clearTimeout(this.modalCloseTimer);
            this.modalCloseTimer = null;
            this.$refs.dialog?.classList.remove('is-closing');
            this.modalClosing = false;
            this.resetExperience();
            this.modalOpen = false;
            this.$nextTick(() => this.$refs.launch?.focus());
        },

        resetExperience() {
            this.stopReverse();
            this.clearContentTimers();

            this.cancelWarmup();
            this.cancelPendingPlayback();
            this.retainedVideos.forEach(index => this.getVideo(index)?.pause());
            this.failedIndex = null;
            this.videoVisible = false;

            this.activeIndex = null;
            this.playing = false;
            this.reversing = false;
            this.contentIndex = null;
            this.contentVisible = false;
            this.openIndex = null;
            this.mobileDetailsOpen = false;
        },

        initStageSizing() {
            const wrap = this.$refs.stageWrap;
            if (!wrap) return;

            this.stageResizeObserver = new ResizeObserver(() =>
                this.updateStageSize()
            );
            this.stageResizeObserver.observe(wrap);
            this.updateStageSize();
        },

        updateStageSize() {
            const wrap = this.$refs.stageWrap;
            if (!wrap) return;

            const { width, height } = wrap.getBoundingClientRect();
            if (!width || !height) return;

            if (width / height > this.posterAspect) {
                this.stageWidth = width;
                this.stageHeight = width / this.posterAspect;
            } else {
                this.stageHeight = height;
                this.stageWidth = height * this.posterAspect;
            }
        },

        stageStyle() {
            if (!this.stageWidth || !this.stageHeight) {
                return { width: '100%', height: '100%' };
            }

            return {
                width: `${this.stageWidth}px`,
                height: `${this.stageHeight}px`,
            };
        },

        getVideo(index) {
            return this.$root.querySelector(`[data-video-index="${index}"]`);
        },

        usesMobileAnimation() {
            return window.matchMedia('(max-width: 47.999rem)').matches
                || window.matchMedia('(hover: none) and (pointer: coarse)').matches;
        },

        videoSource(building) {
            if (!building) return '';
            return this.usesMobileAnimation() && building.mobileSrc
                ? building.mobileSrc
                : building.src;
        },

        allowsWarmup() {
            const connection = navigator.connection;
            return !connection?.saveData &&
                !['slow-2g', '2g', '3g'].includes(connection?.effectiveType);
        },

        cancelWarmup() {
            if (this.warmTimer) clearTimeout(this.warmTimer);
            this.warmTimer = null;
        },

        warm(index) {
            this.cancelWarmup();
            if (!this.modalOpen || this.playing || !this.videoSource(this.buildings[index]) || !this.allowsWarmup()) return;
            this.warmTimer = setTimeout(() => {
                this.warmTimer = null;
                if (this.modalOpen && !this.playing) this.prepareVideo(index, 'metadata');
            }, 200);
        },

        releaseVideo(index) {
            const video = this.getVideo(index);
            if (!video?.hasAttribute('src')) return;
            video.pause();
            video.removeAttribute('src');
            video.load(); // Abort the old resource and release its decoder.
        },

        prepareVideo(index, preload = 'auto') {
            const building = this.buildings[index];
            const video = this.getVideo(index);
            if (!building || !video) return null;
            const source = this.videoSource(building);
            if (!source) return null;

            video.preload = preload;
            if (video.getAttribute('src') !== source) {
                video.src = source;
                video.load();
            }
            this.retainedVideos = this.retainedVideos.filter(i => i !== index);
            this.retainedVideos.push(index);
            const limit = this.usesMobileAnimation() ? 1 : 2;
            while (this.retainedVideos.length > limit) {
                this.releaseVideo(this.retainedVideos.shift());
            }
            return video;
        },

        cancelPendingPlayback() {
            this.playbackRunId++;
            if (this.loadTimer) clearTimeout(this.loadTimer);
            this.loadTimer = null;
            if (this.frameCallbackId !== null) {
                this.frameVideo?.cancelVideoFrameCallback?.(this.frameCallbackId);
            }
            this.frameCallbackId = null;
            this.frameVideo = null;
            this.loading = false;
        },

        handleWaiting(index) {
            if (this.activeIndex !== index || !this.videoVisible || this.reversing) return;
            this.loading = true;
            if (this.loadTimer) clearTimeout(this.loadTimer);
            const runId = this.playbackRunId;
            this.loadTimer = setTimeout(() => this.failPlayback(index, runId), 15000);
        },

        handlePlaying(index) {
            if (this.activeIndex !== index || !this.videoVisible || this.reversing) return;
            if (this.loadTimer) clearTimeout(this.loadTimer);
            this.loadTimer = null;
            this.loading = false;
        },

        failPlayback(index, runId = this.playbackRunId) {
            if (runId !== this.playbackRunId || this.activeIndex !== index) return;
            this.getVideo(index)?.pause();
            this.stopReverse();
            this.cancelPendingPlayback();
            this.videoVisible = false;
            this.failedIndex = index;
            this.playing = false;
            this.activeIndex = null;
            this.hideContent();
            this.releaseVideo(index);
        },

        hotspotStyle(building, index) {
            // Give every hotspot a noticeably different pulse cadence and
            // stagger its first pulse so the rings never move in lockstep.
            const pulseDuration = 3200 + ((index * 613) % 3600);
            const pulseDelay = 250 + ((index * 1450) % 3800);

            return {
                left: `${building.x}%`,
                top: `${building.y}%`,
                '--hotspot-enter-delay': `${index * 70}ms`,
                '--hotspot-pulse-duration': `${pulseDuration}ms`,
                '--hotspot-pulse-delay': `${pulseDelay}ms`,
            };
        },

        openMobileDetails(index) {
            this.back();
            this.openIndex = index;
            this.warm(index);
            this.$nextTick(() => {
                this.mobileDetailsOpen = true;
            });
        },

        closeMobileDetails() {
            if (!this.mobileDetailsOpen) return;

            this.back();
            this.cancelWarmup();
            this.mobileDetailsOpen = false;
            const reduceMotion = window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            ).matches;
            if (reduceMotion) this.openIndex = null;
        },

        handleMobileDetailsTransitionEnd(event) {
            if (
                ['transform', 'translate'].includes(event.propertyName) &&
                !this.mobileDetailsOpen
            ) {
                this.openIndex = null;
            }
        },

        get openBuilding() {
            if (this.openIndex === null) return null;
            return this.buildings[this.openIndex] ?? null;
        },

        get activeContent() {
            if (this.contentIndex === null) return null;
            return this.buildings[this.contentIndex]?.content ?? null;
        },

        clearContentTimers() {
            if (this.contentShowTimer) clearTimeout(this.contentShowTimer);
            if (this.contentHideTimer) clearTimeout(this.contentHideTimer);
            this.contentShowTimer = null;
            this.contentHideTimer = null;
        },

        showContent(index) {
            this.clearContentTimers();
            if (!this.buildings[index]?.content) return;

            this.contentShowTimer = setTimeout(() => {
                this.contentShowTimer = null;
                if (this.activeIndex !== index || this.reversing) return;
                this.contentIndex = index;
                this.contentVisible = true;
            }, this.contentDelay);
        },

        hideContent() {
            this.clearContentTimers();
            if (this.contentIndex === null) return;

            this.contentVisible = false;
            this.contentHideTimer = setTimeout(() => {
                this.contentHideTimer = null;
                this.contentIndex = null;
            }, this.contentFade);
        },

        select(index) {
            if (!this.modalOpen || this.playing || !this.buildings[index]) return;
            this.cancelWarmup();
            this.stopReverse();
            this.cancelPendingPlayback();
            if (!this.videoSource(this.buildings[index])) {
                this.clearContentTimers();
                this.failedIndex = null;
                this.openIndex = index;
                this.activeIndex = index;
                this.videoVisible = false;
                this.contentIndex = index;
                this.contentVisible = true;
                return;
            }
            const video = this.prepareVideo(index);
            if (!video) return;

            const runId = this.playbackRunId;
            this.failedIndex = null;
            this.openIndex = index;
            this.activeIndex = index;
            this.playing = true;
            this.loading = true;
            this.videoVisible = false;
            this.loadTimer = setTimeout(() => this.failPlayback(index, runId), 15000);

            const reveal = () => {
                if (runId !== this.playbackRunId || !this.modalOpen) return;
                if (this.loadTimer) clearTimeout(this.loadTimer);
                this.loadTimer = null;
                this.frameCallbackId = null;
                this.frameVideo = null;
                this.loading = false;
                this.videoVisible = true;
                this.showContent(index);
            };

            try {
                video.currentTime = 0;
                // Call play within the user gesture, including on iOS.
                const playPromise = video.play();
                if (video.requestVideoFrameCallback) {
                    this.frameVideo = video;
                    this.frameCallbackId = video.requestVideoFrameCallback(reveal);
                }
                playPromise.then(() => {
                    if (!video.requestVideoFrameCallback) reveal();
                }).catch(() => this.failPlayback(index, runId));
            } catch {
                this.failPlayback(index, runId);
            }
        },

        back() {
            if (this.activeIndex === null || this.reversing) return;
            if (!this.playing) {
                this.hideContent();
                this.finishReverse();
                return;
            }
            const video = this.getVideo(this.activeIndex);
            if (!video) return;

            this.hideContent();
            video.pause();
            if (this.loading || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                this.cancelPendingPlayback();
                this.finishReverse();
                return;
            }
            this.reversing = true;
            const runId = ++this.reverseRunId;
            this.reverse(video, runId);
        },

        reverse(video, runId) {
            const startTime = video.currentTime;
            const startTs = performance.now();
            const interval = 1000 / (this.usesMobileAnimation() ? 15 : 30);
            let lastSeek = startTs - interval;
            const step = timestamp => {
                if (this.reverseRunId !== runId) return;
                const nextTime = Math.max(0, startTime -
                    ((timestamp - startTs) / 1000) * this.reverseSpeed);

                // Permit one seek in flight. End on wall-clock time even if
                // a slow decoder cannot keep up, rather than extending Back.
                if (nextTime <= 0) {
                    this.returnToPoster(video, runId);
                    return;
                }
                if (!video.seeking && timestamp - lastSeek >= interval) {
                    try {
                        video.currentTime = nextTime;
                        lastSeek = timestamp;
                    } catch {
                        this.finishReverse();
                        return;
                    }
                }
                this.rafId = requestAnimationFrame(step);
            };
            this.rafId = requestAnimationFrame(step);
        },

        returnToPoster(video, runId) {
            // Do not swap to the still while the last decoded frame is
            // slightly zoomed in. Land on the exact opening frame first.
            const finish = () => {
                if (this.reverseRunId !== runId) return;
                this.rafId = requestAnimationFrame(() => {
                    this.rafId = requestAnimationFrame(() => {
                        if (this.reverseRunId === runId) this.finishReverse();
                    });
                });
            };
            if (!video.seeking && video.currentTime === 0) {
                finish();
                return;
            }
            this.reverseSeekVideo = video;
            this.reverseSeekHandler = finish;
            video.addEventListener('seeked', finish, { once: true });
            this.reverseSeekTimer = setTimeout(() => {
                if (this.reverseRunId === runId) this.finishReverse();
            }, 1000);
            try {
                video.currentTime = 0;
            } catch {
                this.finishReverse();
            }
        },

        stopReverse() {
            this.reverseRunId++;
            if (this.reverseSeekTimer) clearTimeout(this.reverseSeekTimer);
            this.reverseSeekVideo?.removeEventListener('seeked', this.reverseSeekHandler);
            this.reverseSeekTimer = null;
            this.reverseSeekVideo = null;
            this.reverseSeekHandler = null;
            if (this.rafId) cancelAnimationFrame(this.rafId);
            this.rafId = null;
            this.reversing = false;
        },

        finishReverse() {
            this.stopReverse();
            this.rafId = null;
            this.playing = false;
            this.videoVisible = false;
            this.activeIndex = null;
        },
    };
}
